import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import Welcome from './pages/Welcome';
import Auth from './pages/Auth';
import ProfileStep1 from './pages/ProfileStep1';
import ProfileStep2 from './pages/ProfileStep2';
import Profile from './pages/Profile';
import LoanLimits from './pages/LoanLimits';
import ServiceFeeSelection from './pages/ServiceFeeSelection';
import PaymentInstructions from './pages/PaymentInstructions';
import PaymentVerification from './pages/PaymentVerification';
import ApplicationStatus from './pages/ApplicationStatus';
import Dashboard from './pages/Dashboard';
import { authStateListener } from './services/auth';
import { getUserProfile } from './services/firestore';
import useAuthStore from './stores/authStore';
import './index.css';

function App() {
  const { setUser, setProfile, clearAuth, setLoading } = useAuthStore();

  useEffect(() => {
    const unsubscribe = authStateListener(async (user) => {
      setLoading(true);
      
      if (user) {
        setUser(user);
        
        // Load user profile from Firebase
        try {
          const profileResult = await getUserProfile(user.uid);
          if (profileResult.success) {
            setProfile(profileResult.profile);
          } else {
            // Profile doesn't exist yet, user needs to complete it
            setProfile(null);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
          setProfile(null);
        }
      } else {
        clearAuth();
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setProfile, clearAuth, setLoading]);

  return (
    <Router>
      <Layout>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/auth" element={<Auth />} />
          
          {/* Protected routes - require authentication */}
          <Route 
            path="/profile-step-1" 
            element={
              <ProtectedRoute>
                <ProfileStep1 />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile-step-2" 
            element={
              <ProtectedRoute>
                <ProfileStep2 />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected routes - require completed profile */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute requireProfile={true}>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/loan-limits" 
            element={
              <ProtectedRoute requireProfile={true}>
                <LoanLimits />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/service-fee-selection" 
            element={
              <ProtectedRoute requireProfile={true}>
                <ServiceFeeSelection />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment-instructions" 
            element={
              <ProtectedRoute requireProfile={true}>
                <PaymentInstructions />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment-verification" 
            element={
              <ProtectedRoute requireProfile={true}>
                <PaymentVerification />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/application-status" 
            element={
              <ProtectedRoute requireProfile={true}>
                <ApplicationStatus />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute requireProfile={true}>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;